import events from '@/events/events'
import Tenant, { TenantMetric } from '@/types/Tenant.interface'

interface State {
    tenant: Tenant,
    tenantApiUrl: string,
    tenantKey: string,
    tenantMetrics: TenantMetric[]
    tutorialItems: Array<string>
}

const defaults = () => {
    return {
        tenant: null,       //  Our current tenant
        tenantApiUrl: null, //  The subdomain based url
        tenantKey: null,     // Unique key to reference our tenant with
        tutorialItems: [
            'createdAccount',
            'contactsImported',
            'sentMessage',
            'newNumber',
            'sentInvitation',
            'meetingSetUp'
        ],
        tenantMetrics: null
    }
}

export default {
    state: defaults(),
    mutations: {
        tenantSet(state: State, tenant: Tenant): void {

            //  Check if we are setting a new tenant
            const isNew = !tenant || !state.tenant || (state.tenant.id !== tenant.id)

            //  If the tenant exists...
            if (tenant) {
                //  Update the state
                state.tenant = tenant

                //  Assemble the tenant's API endpoint
                const api = process.env.VUE_APP_TEST_URL ? `${process.env.VUE_APP_TEST_URL}/${tenant.subdomain}` : `${process.env.VUE_APP_URL_API}/${tenant.subdomain}`

                //  Set the tenant's API endpoint
                state.tenantApiUrl = api

                //  Save the tenant to local storage
                sessionStorage.setItem('tenant', JSON.stringify({ id: tenant.id }))

                //  If this is a new tenant, publish the tenantSet event
                if (isNew) events.onTenantSet(tenant)
            }

            //  Else, the tenant is null, so remove session storage
            else {
                //  Get a copy of the current tenant (the one being removed)
                const toRemove = state.tenant || null

                //  Update the state
                state.tenant = tenant

                //  Remove the tenant from session storage
                sessionStorage.removeItem('tenant')

                //  Publish the tenant removed event
                events.onTenantRemoved(toRemove)
            }
        },
        tenantKeySet(state: State, key: string): void {
            state.tenantKey = key
        },
        tenantMetricsSet(state: State, metrics: TenantMetric[]): void {
            //  We use object assign so we don't need to do a FULL set
            const updated = Object.assign({}, state.tenantMetrics, metrics)

            state.tenantMetrics = updated
        }
    },
    actions: {
        tenantAddressUpdate({ commit, getters }, tenant: Tenant): Tenant {
            const url = `${getters.api}/tenant/address`
            return window.axios.put(url, tenant).then(response => {
                commit('tenantSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        //  Payload contains the tenant's id {"tenantId": value, "username": email}
        tenantConnectToV2({ commit, getters }, payload: Object) {
            //  Assemble the API endpoint
            const url = `${getters.api}/tenant/migrate/connect`

            return window.axios.post(url, payload).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        tenantInitialStateGet({ commit, dispatch, getters }): boolean {
            //  Fetch the stripe customer
            dispatch('stripeCustomerGet')

            const url = `${getters.api}/initial_state`
            return window.axios.get(url).then(response => {
                commit('brandSet', response.data.brand)
                commit('campaignCountSet', response.data.campaign_count)
                commit('chatbotsCountSet', response.data.chatbot_count)
                commit('consentSet', response.data.consent)
                dispatch('currentMembershipSet', response.data.current_membership)
                commit('extensionOptionsSet', response.data.extension_options)
                commit('formCountSet', response.data.form_count)
                commit('integrationsSet', response.data.integrations)
                commit('notificationsUnreadCountSet', response.data.notification_count)
                commit('phoneOrderSet', response.data.phone_order)
                commit('rolesSet', response.data.roles)
                commit('settingsSet', response.data.settings)
                commit('statesSet', response.data.states)
                commit('optedOutSet', response.data.opted_out)
                commit('smsAccountSet', response.data.sms_account)
                commit('storageLimitSet', response.data.storage_limit)
                commit('storageUsedSet', response.data.storage_used)
                commit('templateCountSet', response.data.template_count)
                commit('userCountSet', response.data.user_count)
                commit('xeroAccountSet', response.data.xero_account)
                return true
            }).catch(() => {
                return false
            })
        },
        tenantGet({ commit }, id: number): Tenant {
            return window.axios.get(`/tenant/${id}`).then(response => {
                commit('tenantSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        tenantMetrics ({ commit, getters }, payload): TenantMetric {
            //  Assemble the API endpoint
            const url = `${getters.api}/tenant/metrics`
            
            //  Send our request
            return window.axios.post(url, payload).then(response => {
                //  Set our metrics
                commit('tenantMetricsSet', response.data)

                //  Return our metrics
                return response.data
            }).catch(() => null)
        },
        //  Payload just contains the tenant's id and a boolean value for every item a user can export so we know what they do/don't want to export
        tenantMigrateData({ commit, getters }, payload: Object) {
            const url = `${getters.api}/tenant/migrate`

            return window.axios.post(url, payload).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        // Payload contains tenant id and tenant phone number to transfer to v2
        tenantMigratePhone({ commit, getters }, payload: Object) {
            const url = `${getters.api}/tenant/migrate/phone`

            return window.axios.post(url, payload).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        tenantPost({ commit }, tenant: Tenant): Tenant {
            return window.axios.post('/tenant', tenant).then(response => {
                commit('tenantSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        tenantPut({ commit, getters }, tenant: Tenant): Tenant {
            const url = `${getters.api}/tenant`
            return window.axios.put(url, tenant).then(response => {
                commit('tenantSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        tenantReset({ commit }): void {
            //  Wipe all tenant data from the local store
            commit('appLayoutReset')
            commit('appNotificationBarReset')
            commit('appSyncNotificationsReset')
            commit('autorespondersReset')
            commit('brandReset')
            commit('broadcastsReset')
            commit('broadcastsViewReset')
            commit('campaignsReset')
            commit('chatbotsReset')
            commit('chatbotsViewReset')
            commit('consentReset')
            commit('contactsReset')
            commit('customFieldsReset')
            commit('extensionOptionsReset')
            commit('formsReset')
            commit('formsViewReset')
            commit('groupsReset')
            commit('helpModalReset')
            commit('importReset')
            commit('inboxReset')
            commit('invitationsReset')
            commit('invoicesReset')
            commit('invoiceCampaignReset')
            commit('listsReset')
            commit('mediaReset')
            commit('mediaViewReset')
            commit('messagesReset')
            commit('messageTextFormReset')
            commit('notificationsUnreadReset')
            commit('notificationsReset')
            commit('officeHoursReset')
            commit('personReset')
            commit('personsAutocompleteReset')
            commit('personsDuplicatesReset')
            commit('personsQueueReset')
            commit('personsReset')
            commit('printQueueReset')
            commit('qboReset')
            commit('rolesReset')
            commit('routesReset')
            commit('routesViewReset')
            commit('searchBarReset')
            commit('settingsReset')
            commit('smsAccountReset')
            commit('statesReset')
            commit('stripeReset')
            commit('subscriptionReset')
            commit('templatesReset')
            commit('usersReset')
            commit('teammatesReset')
            commit('xeroReset')
        }
    },
    getters: {
        api(state: State): string {
            return state.tenantApiUrl
        },
        contactNameOptional(state: State): boolean {
            //  Determine is contact names are optional in this tenant (eg. Xero and QBO intergrations require a name)
            const optional =
                state.tenant.data_provider !== 'QBO' &&
                state.tenant.data_provider !== 'XERO'

            return optional
        },
        dataProvider(state: State): string {
            return state.tenant.data_provider
        },
        tenant(state: State): Tenant {
            return state.tenant
        },
        tenantKey(state: State): string {
            return state.tenantKey
        },
        tenantMetrics(state: State): TenantMetric[] {
            return state.tenantMetrics
        },
        //  We append 'createdAccount' to everything because we never actually set it in the DB
        tutorialItemsComplete(state: State): Array<string> {
            //  Grab our tutorial items
            const items = state.tutorialItems

            //  Grab our tenant
            const tenant = state.tenant

            //  If no tenant, return our items
            if (!tenant) return items

            //  If tenant is active, just return our tutorial items
            if (tenant.account_status === 'active') return items

            //  If tenant tutorial is null, return empty array
            if (!tenant.tutorial) return ['createdAccount']

            //  Else filter our array
            const filtered = items.filter(x => tenant.tutorial[x]) || []

            //  Make sure we have createdAccount in this
            return [...filtered, 'createdAccount']
        },
        tutorialItems(state: State): Array<string> {
            return state.tutorialItems
        }
    }
}
